<template>
  <div>
    <div class="flex items-start mb-4 bg-white shadow sm:rounded-t-md py-6 px-4 sm:px-6 lg:px-8">
      <div class="flex flex-row justify-between items-center">
        <Datepicker
          class="text-xs rounded-lg border-gray-300 shadow-md text-sm w-full sm:w-64 rounded-md"
          v-model="date"
          range
          multiCalendars
          :presetRanges="presetRanges"
          placeholder="Sélectionner une période"
          autoApply
          :enableTimePicker="false"
          :format="datePickerFormat"
          locale="fr"
          :max-date="new Date()"
          :esc-close="false"
        />
      </div>
    </div>

    <div v-if="storeId && stats">
      <div class="min-h-5 mb-5" v-if="backToStoresBtn">
        <a
          href="#"
          class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          v-on:click="storeIdSelected(null)"
        >
          Précédent
        </a>
      </div>
      <SellerAnalytics
        v-if="storeId && stats"
        :currentMonth="currentMonth"
        :stats="stats"
        :total="total"
        @storeIdSelected="storeIdSelected"
        @sortChanged="onSellersSortChange"
      />
    </div>
    <StoreAnalytics
      v-else-if="stats"
      :currentMonth="currentMonth"
      :stats="stats"
      :total="total"
      @storeIdSelected="storeIdSelected"
      @sortChanged="onStoresSortChange"
    />
    <Loading v-if="isLoading" />
  </div>
</template>

<script>
import Datepicker from "@vuepic/vue-datepicker";
import { Loading } from "@estaly/ui";

import StoreAnalytics from "@/components/analytics/retail/StoreAnalytics.vue";
import SellerAnalytics from "@/components/analytics/retail/SellerAnalytics.vue";

import AnalyticsService from "@/api/services/analytics";

import "@vuepic/vue-datepicker/dist/main.css";
import { startOfMonth, format } from "date-fns";
import {
  currentMonth,
  previousMonth,
  currentWeek,
  previousWeek,
  currentMonthRange,
  currentWeekRange,
  previousMonthRange,
  previousWeekRange,
} from "@estaly/ui/src/utils/date.js";
import { getQueryParams, setQueryParams } from "@estaly/ui/src/utils/query_params.js";
import { mapGetters } from "vuex";

export default {
  components: {
    Datepicker,
    StoreAnalytics,
    SellerAnalytics,
    Loading,
  },

  data: () => {
    return {
      isLoading: false,
      storeId: null,
      storesSortParams: {
        column: null,
        order: null,
      },
      sellersSortParams: {
        column: null,
        order: null,
      },
      date: [],
      currentMonth: "",
      presetRanges: [
        {
          id: 1,
          label: "Mois en cours",
          range: currentMonthRange(),
        },
        {
          id: 2,
          label: "Semaine en cours",
          range: currentWeekRange(),
        },
        {
          id: 3,
          label: "Mois précédent",
          range: previousMonthRange(),
        },
        {
          id: 4,
          label: "Semaine précédente",
          range: previousWeekRange(),
        },
      ],
    };
  },

  computed: {
    ...mapGetters("store", ["selectedStoreId"]),

    backToStoresBtn() {
      return !this.selectedStoreId;
    },
  },

  created() {
    const endDate = new Date();
    const startDate = startOfMonth(new Date());
    this.date = [startDate, endDate];

    if (this.selectedStoreId) {
      this.storeId = this.selectedStoreId;
    }
  },

  methods: {
    loadStoreIdFromQueryParams() {
      const storeId = getQueryParams().storeId;
      if (storeId) this.storeId = storeId;
    },

    datePickerFormat(date) {
      const startDate = date[0];
      const endDate = date[1];

      const startDay = startDate.getDate();
      const startMonth = startDate.getMonth() + 1;
      const startYear = startDate.getFullYear();

      const endDay = endDate.getDate();
      const endMonth = endDate.getMonth() + 1;
      const endYear = endDate.getFullYear();

      if (startDate && endDate) {
        if (startDate instanceof Date && endDate instanceof Date) {
          if (currentMonth(startDate, endDate)) {
            return "Mois en cours";
          }
          if (previousMonth(startDate, endDate)) {
            return "Mois précédent";
          }
          if (currentWeek(startDate, endDate)) {
            return "Semaine en cours";
          }
          if (previousWeek(startDate, endDate)) {
            return "Semaine précédente";
          }
        }
      }

      return `Du ${startDay}/${startMonth}/${startYear} au ${endDay}/${endMonth}/${endYear}`;
    },

    updateCurrentMonth(endDate) {
      const monthNames = [
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Décembre",
      ];
      const month = monthNames[endDate.getMonth()];
      const year = endDate.getFullYear();
      this.currentMonth = `${month} ${year}`;
    },

    async getAnalytics() {
      const start = Array.isArray(this.date) && this.date[0] ? format(this.date[0], "yyyy-MM-dd") : null;
      const end = Array.isArray(this.date) && this.date[1] ? format(this.date[1], "yyyy-MM-dd") : null;
      const sortParams = this.retrieveSortParams();

      setQueryParams({ tab: "retail", storeId: this.storeId });
      let response;
      this.isLoading = true;

      if (this.storeId) {
        response = await AnalyticsService.getRetailSellersData(this.storeId, start, end, sortParams);
      } else {
        response = await AnalyticsService.getRetailStoresData(start, end, sortParams);
      }

      this.isLoading = false;
      if (response?.success) {
        const data = response.data;
        this.stats = data.stats;
        this.total = data.total;
      }
    },

    storeIdSelected(storeId) {
      if (this.storeId == storeId) return;

      this.storeId = storeId;
      this.getAnalytics();
    },

    async onStoresSortChange(sortParams) {
      this.storesSortParams = sortParams;
      this.getAnalytics();
    },

    async onSellersSortChange(sortParams) {
      this.sellersSortParams = sortParams;
      this.getAnalytics();
    },

    async resetToBeginning() {
      if (!this.date) return;

      this.date = null;
      this.getAnalytics();
    },

    retrieveSortParams() {
      if (this.storeId) {
        return this.sellersSortParams;
      } else {
        return this.storesSortParams;
      }
    },
  },

  watch: {
    async date(newDate) {
      if (Array.isArray(newDate) && newDate.length > 0 && newDate[0] && newDate[1]) {
        this.updateCurrentMonth(newDate[1]);
      }

      this.getAnalytics();
    },

    "$route.query"() {
      this.loadStoreIdFromQueryParams();
    },
  },
};
</script>
